import React, { useContext, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import 'leaflet-routing-machine';
import './ViewWeather.css';
import { LoadingContext } from "../../../composants/loadingContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudMoonRain, faList, faSearch, faMapMarkerAlt, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import Loader from "../../../composants/loader/Loader";
import { Button, message, Select, Tag, Card, Row, Col, Space, Typography, Divider, DatePicker } from "antd";
import { SearchOutlined, EnvironmentOutlined, CloudOutlined, CalendarOutlined } from '@ant-design/icons';
import axiosInstance from "../../../configurations/axiosInstance/AxiosInstance";
import dayjs from 'dayjs';
import 'dayjs/locale/fr';

dayjs.locale('fr');

const { Title, Text } = Typography;

export default function ViewWeather() {
  const { loading, setLoading } = useContext(LoadingContext);
  const [current, setCurrent] = useState("");
  const [currentDate, setCurrentDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [departements, setDepartements] = useState([]);
  const [departement, setDepartement] = useState(null);
  const mapRef = useRef(null);
  const leafletMap = useRef(null);

  const greenIcon = L.icon({
    iconUrl: process.env.PUBLIC_URL + "/images/location.png",
    iconSize: [38, 95],
    shadowSize: [50, 64],
    iconAnchor: [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76]
  });
  const onSearch = (value) => {
    const searchValue = String(value).trim();
    const dep = departements.find((dep) => dep.id === searchValue);
    const dep1 = departements.find((dep) => dep.id === '1');
    if (dep != undefined) {
      setDepartement(dep);
    } else {
      setDepartement(null);
    }
  };

  const onDateChange = (date) => {
    if (date) {
      setCurrentDate(date.format('YYYY-MM-DD'));
    } else {
      setCurrentDate(dayjs().format('YYYY-MM-DD'));
    }
  };
  const callBack = () => {
    if (current === "") {
      message.warning("Veuillez sélectionner une période (Matin, Journée ou Soir)");
      return;
    }
    if (departement == null) {
      message.warning("Veuillez sélectionner un département");
      return;
    }
    fetchWeather(current, departement.id);
  }
  const fetchDepartement = () => {
    setLoading(true);
    axiosInstance().get(`public/locality/list/dep`).then((response) => {
      if (response.data == []) {
        setDepartements([]);
      } else {
        setDepartements(response.data);
      }
      setLoading(false);
    }).catch((error) => {
      message.error("Error");
      setDepartements([]);
      setLoading(false);
    });
  }
  const fetchWeather = (value, id) => {
    setLoading(true);
    let url = `public/weather/list?weatherTime=${value}&dep=${id}&date=${currentDate}`;
    axiosInstance().get(url).then((response) => {
      let data = response.data;
      if (data && data.length > 0) {
        viewMarker(data);
      } else {
        message.info("Aucune donnée trouvée pour cette période et ce département.");
      }
      setLoading(false);
    }).catch((error) => {
      const errorMsg = error.response?.data?.error || "Erreur lors de la récupération des données météo";
      message.error(errorMsg);
      setLoading(false);
    });
  }
  useEffect(() => {
    if (mapRef.current && !leafletMap.current) {
      const openstreetmap = L.tileLayer(
        "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          maxZoom: 19,
          attribution:
            '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }
      );

      const googleStreets = L.tileLayer(
        "http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}",
        {
          maxZoom: 20,
          subdomains: ["mt0", "mt1", "mt2", "mt3"],
        }
      );

      const googleHybrid = L.tileLayer(
        "http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
        {
          maxZoom: 20,
          subdomains: ["mt0", "mt1", "mt2", "mt3"],
        }
      );

      // --- Créer la carte ---
      const map = L.map(mapRef.current, {
        center: [7.3697, 12.3547],
        zoom: 6,
        layers: [openstreetmap], // par défaut OSM
      });

      // --- Control des couches ---
      const baseMaps = {
        "Open Street Map": openstreetmap,
        "Google Streets": googleStreets,
        "Google Map Hybrid": googleHybrid,
      };

      L.control.layers(baseMaps).addTo(map);

      leafletMap.current = map;

      // localiser l'utilisateur
      map.locate({ setView: true, maxZoom: 6 });
      map.on("locationfound", (e) => {
        L.circle(e.latlng, e.accuracy).addTo(map);
      });

      // Force le redimensionnement de la carte après un court délai
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }
    fetchDepartement();
  }, []);

  const viewMarker = (datas) => {
    if (leafletMap.current && datas.length > 0) {
      // supprimer anciens markers avant de recréer
      leafletMap.current.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.Circle) {
          leafletMap.current.removeLayer(layer);
        }
      });

      // recréer la couche OSM
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
        leafletMap.current
      );

      datas.forEach((item) => {
        const popup = `
              <div style="min-width: 280px; padding: 12px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
                <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 12px; border-radius: 8px 8px 0 0; margin: -12px -12px 12px -12px;">
                  <h3 style="margin: 0; font-size: 16px; font-weight: 600; display: flex; align-items: center; gap: 8px;">
                    <span style="font-size: 20px;">🌤️</span>
                    Prévision Météo
                  </h3>
                </div>
                
                <div style="background: #f8f9fa; padding: 10px; border-radius: 6px; margin-bottom: 12px;">
                  <div style="display: flex; align-items: center; gap: 6px; color: #495057; font-size: 13px;">
                    <span style="font-size: 16px;">📍</span>
                    <strong>${item?.communes?.name}</strong>
                  </div>
                  <div style="color: #6c757d; font-size: 12px; margin-left: 22px;">
                    ${item?.communes?.departement?.name}, ${item?.communes?.departement?.region?.name}
                  </div>
                </div>

                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                  <div style="background: #fff3cd; padding: 8px; border-radius: 6px; border-left: 3px solid #ffc107;">
                    <div style="font-size: 11px; color: #856404; margin-bottom: 2px;">Température</div>
                    <div style="font-size: 18px; font-weight: 700; color: #856404;">${item.temptation}</div>
                  </div>
                  
                  <div style="background: #d1ecf1; padding: 8px; border-radius: 6px; border-left: 3px solid #17a2b8;">
                    <div style="font-size: 11px; color: #0c5460; margin-bottom: 2px;">Humidité</div>
                    <div style="font-size: 18px; font-weight: 700; color: #0c5460;">${item.humidity}</div>
                  </div>
                  
                  <div style="background: #d4edda; padding: 8px; border-radius: 6px; border-left: 3px solid #28a745; grid-column: span 2;">
                    <div style="font-size: 11px; color: #155724; margin-bottom: 2px;">☀️ Ciel</div>
                    <div style="font-size: 13px; font-weight: 600; color: #155724;">${item.skyDescription}</div>
                  </div>
                  
                  <div style="background: #cce5ff; padding: 8px; border-radius: 6px; border-left: 3px solid #007bff;">
                    <div style="font-size: 11px; color: #004085; margin-bottom: 2px;">�️ Vent</div>
                    <div style="font-size: 13px; font-weight: 600; color: #004085;">${item.wind}</div>
                  </div>
                  
                  <div style="background: #e2e3e5; padding: 8px; border-radius: 6px; border-left: 3px solid #6c757d;">
                    <div style="font-size: 11px; color: #383d41; margin-bottom: 2px;">🌧️ Pluie</div>
                    <div style="font-size: 13px; font-weight: 600; color: #383d41;">${item.rain}</div>
                  </div>
                </div>
              </div>
          `;
        let latitude = (item.latitude == null || item.latitude == undefined) ? 0 : item.latitude;
        let longitude = (item.longitude == null || item.longitude == undefined) ? 0 : item.longitude;

        L.marker(
          [latitude, longitude],
          { icon: greenIcon }
        ).addTo(leafletMap.current)
          .bindPopup(popup, { maxWidth: 320 });
      });
    }
  }

  const setValue = (value) => {
    if (value == "") {
      setCurrent("");
    } else {
      setCurrent(value);
    }
  }

  return (
    <>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <section className="col-lg-12">
              <Card
                bordered={false}
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  marginBottom: '24px'
                }}
              >
                <Row gutter={[16, 16]} align="middle" style={{ color: 'white' }}>
                  <Col xs={24} md={12}>
                    <Space direction="vertical" size={4}>
                      <Space align="center" size={12}>
                        <CloudOutlined style={{ fontSize: '32px' }} />
                        <Title level={3} style={{ margin: 0, color: 'white' }}>
                          Prévisions Météorologiques
                        </Title>
                      </Space>
                      <Space align="center" style={{ marginLeft: '44px' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <Text style={{ color: 'rgba(255,255,255,0.95)', fontSize: '15px' }}>
                          {dayjs(currentDate).format('dddd D MMMM YYYY')}
                        </Text>
                      </Space>
                    </Space>
                  </Col>
                </Row>
              </Card>

              <Card
                bordered={false}
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                <Space direction="vertical" size={20} style={{ width: '100%' }}>
                  {/* Sélection de la date */}
                  <div>
                    <Text strong style={{ fontSize: '15px', marginBottom: '12px', display: 'block' }}>
                      <CalendarOutlined style={{ marginRight: '8px', color: '#667eea' }} />
                      Sélectionnez une date
                    </Text>
                    <DatePicker
                      value={dayjs(currentDate)}
                      onChange={onDateChange}
                      format="DD/MM/YYYY"
                      size="large"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        borderRadius: '8px',
                      }}
                      placeholder="Choisir une date"
                      allowClear={false}
                    />
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* Sélection de la période */}
                  <div>
                    <Text strong style={{ fontSize: '15px', marginBottom: '12px', display: 'block' }}>
                      📅 Sélectionnez la période de la journée
                    </Text>
                    <Space size={[8, 8]} wrap>
                      {["MORNING", "NOON", "EVENING"].map((value) => (
                        <Tag
                          style={{
                            cursor: 'pointer',
                            padding: '8px 16px',
                            fontSize: '14px',
                            borderRadius: '8px',
                            border: 'none',
                            transition: 'all 0.3s',
                            fontWeight: current === value ? '600' : '400'
                          }}
                          onClick={() => setValue(value)}
                          key={value}
                          color={current === value ? "blue" : "default"}
                        >
                          {value === "MORNING" && "🌅 Matin "}
                          {value === "NOON" && "☀️ Journée "}
                          {value === "EVENING" && "🌙 Soir "}
                        </Tag>
                      ))}
                    </Space>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* Sélection du département */}
                  <div>
                    <Text strong style={{ fontSize: '15px', marginBottom: '12px', display: 'block' }}>
                      <EnvironmentOutlined /> Sélectionnez un département
                    </Text>
                    <Select
                      placeholder="🗺️ Sélectionnez un département"
                      onChange={onSearch}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                      }}
                      size="large"
                    >
                      {departements.map((dep) => (
                        <Select.Option
                          key={dep.id}
                          value={dep.id}
                        >
                          📍 {dep.name} ({dep.region.name})
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  {/* Bouton de recherche */}
                  <div>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={callBack}
                      size="large"
                      disabled={!current}
                      style={{
                        borderRadius: '8px',
                        height: '44px',
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        fontWeight: '600',
                        fontSize: '15px'
                      }}
                    >
                      Rechercher les prévisions
                    </Button>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  {/* Carte */}
                  <div style={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid #e8e8e8',
                    position: 'relative'
                  }}>
                    {loading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255,255,255,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                      }}>
                        <Loader />
                      </div>
                    )}
                    <div ref={mapRef} style={{ height: "600px", width: "100%", zIndex: 1 }}></div>
                  </div>
                </Space>
              </Card>
            </section>
          </div>
        </div>
      </section>
    </>
  );

}